import { useEffect, useState } from 'react';
import { api, type OcupacionDiaResponse, type OcupacionSedeDia } from '../lib/api.js';
import { fmtInt, fmtPct } from '../lib/format.js';
import { ChartCard } from './charts/ChartCard.js';

/** Barra de color según qué tan lleno está el hotel. */
function tono(occ: number): string {
  if (occ >= 0.8) return 'bg-emerald-500';
  if (occ >= 0.5) return 'bg-blue-500';
  if (occ >= 0.25) return 'bg-amber-500';
  return 'bg-rose-500';
}

function Fila({ r }: { r: OcupacionSedeDia }) {
  const pct = r.occPct == null ? null : r.occPct * 100;
  return (
    <tr className="border-t border-slate-800">
      <th scope="row" className="py-2 pr-3 text-left font-medium text-slate-200" title={r.nombre}>
        {r.codigo}
      </th>
      <td className="py-2 pr-3">
        {r.sinDato ? (
          <span className="text-xs text-slate-500">sin flash cargado</span>
        ) : (
          <div className="flex items-center gap-2">
            <div className="h-2 w-full min-w-24 overflow-hidden rounded bg-slate-700">
              <div
                className={`h-full ${tono(r.occPct ?? 0)}`}
                style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }}
              />
            </div>
            <span className="w-16 shrink-0 text-right tabular-nums text-slate-100">
              {fmtPct(pct)}
            </span>
          </div>
        )}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums text-slate-100">
        {fmtInt(r.ocupadasSinCh)}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums text-slate-400">{fmtInt(r.house)}</td>
      <td className="py-2 pr-3 text-right tabular-nums text-slate-400">{fmtInt(r.comp)}</td>
      <td className="py-2 pr-3 text-right tabular-nums text-slate-400">
        {fmtInt(r.ocupadasTotales)}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums text-slate-400">
        {fmtInt(r.totalRooms)}
        {r.baseOrigen === 'catalogo' && (
          <span
            className="ml-1 text-[10px] text-amber-400"
            title="El flash no trae Total Rooms; se usa el inventario del catálogo vigente a esa fecha."
          >
            *
          </span>
        )}
      </td>
      <td className="py-2 text-right tabular-nums text-slate-400">{fmtInt(r.disponibles)}</td>
    </tr>
  );
}

/**
 * OCUPACIÓN DEL DÍA, hotel por hotel.
 *
 * El indicador es «Rooms Occupied minus Comp and House Use» sobre el inventario
 * de cada sede — la misma definición que usa el resto del tablero. Comp y House
 * se muestran al lado porque explican la diferencia con el total ocupado, pero
 * no suben el porcentaje.
 *
 * El día lo elige el usuario. Por defecto es el último con dato: el flash de
 * una jornada se sube al día siguiente, así que abrir en «hoy» saldría vacío.
 * Un hotel sin flash cargado sale «sin dato», nunca 0 % — y por eso el total de
 * cadena dice sobre cuántas sedes está calculado.
 */
export function OcupacionDiaPanel({ sedesParam }: { sedesParam?: string }) {
  const [fecha, setFecha] = useState<string | undefined>(undefined);
  const [data, setData] = useState<OcupacionDiaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .ocupacionDia({ fecha, sedes: sedesParam })
      .then((r) => alive && setData(r))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Error cargando ocupación'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [fecha, sedesParam]);

  const selector = (
    <label className="flex items-center gap-2 text-xs text-slate-400">
      Día
      <input
        type="date"
        // Manda lo que el usuario acaba de elegir; si aún no eligió nada, el día
        // lo decide el API (es quien sabe cuál fue el último con flash cargado).
        // Atarlo solo a `data` haría que el campo rebotara al día anterior
        // durante la recarga.
        value={fecha ?? data?.fecha ?? ''}
        min={data?.disponible?.min}
        max={data?.disponible?.max}
        onChange={(e) => e.target.value && setFecha(e.target.value)}
        className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-slate-100"
      />
    </label>
  );

  if (loading && !data)
    return <p className="text-sm text-slate-400">Cargando ocupación del día…</p>;
  if (error) return <p className="rounded-lg bg-rose-950/60 px-3 py-2 text-rose-300">{error}</p>;
  if (!data) return null;

  const c = data.cadena;
  const parcial = c.sedesConDato < c.sedesPedidas;

  return (
    <ChartCard title="Ocupación del día por hotel" action={selector}>
      {data.disponible == null ? (
        <p className="text-sm text-slate-500">
          Todavía no hay ningún día cargado para estas sedes. La ocupación diaria sale del
          Manager-Flash, que se sube en Ingesta.
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-2xl font-semibold text-slate-50">
              {fmtPct(c.occPct == null ? null : c.occPct * 100)}
            </span>
            <span className="text-xs text-slate-400">
              cadena · {fmtInt(c.ocupadasSinCh)} de {fmtInt(c.totalRooms)} habitaciones
            </span>
            {parcial && (
              <span className="text-xs text-amber-400">
                sobre {c.sedesConDato} de {c.sedesPedidas} sedes con flash cargado
              </span>
            )}
          </div>

          {data.ajustada && (
            <p className="mb-3 rounded-lg bg-amber-950/50 px-3 py-2 text-xs text-amber-300">
              El día pedido no tiene flash cargado. Se muestra el último disponible ({data.fecha}).
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-slate-500">
                  <th scope="col" className="pb-2 pr-3 text-left font-normal">
                    Sede
                  </th>
                  <th scope="col" className="pb-2 pr-3 text-left font-normal">
                    % ocupación
                  </th>
                  <th scope="col" className="pb-2 pr-3 text-right font-normal">
                    Ocupadas
                  </th>
                  <th scope="col" className="pb-2 pr-3 text-right font-normal">
                    House
                  </th>
                  <th scope="col" className="pb-2 pr-3 text-right font-normal">
                    Comp
                  </th>
                  <th scope="col" className="pb-2 pr-3 text-right font-normal">
                    Total ocup.
                  </th>
                  <th scope="col" className="pb-2 pr-3 text-right font-normal">
                    Inventario
                  </th>
                  <th scope="col" className="pb-2 text-right font-normal">
                    Disponibles
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <Fila key={r.sedeId} r={r} />
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-[11px] leading-tight text-slate-500">
            «Ocupadas» es <em>Rooms Occupied minus Comp and House Use</em>: es el numerador del
            porcentaje. House y Comp no son venta y no lo suben, pero sí cuentan en «Total ocup.» y
            restan disponibilidad. El denominador es el inventario físico del hotel (incluye
            OOO/OOS).
          </p>
        </>
      )}
    </ChartCard>
  );
}
